/**
 * Complete Form Configuration - SOURCE OF TRUTH
 *
 * This file contains:
 * - FORM_ASSIGNMENTS: Email-based routing logic
 * - Slack channel mappings
 * - Priority calculation logic
 * - SLA calculation logic
 *
 * Note: form-types.ts also contains FORM_ASSIGNMENTS for type context.
 * Both definitions must stay in sync. Update both when making changes.
 *
 * Migrated from winclap-tech-form-f1
 */

import { addBusinessDaysISO } from './dates';
import { FormType } from './form-types';

// ============================================================================
// SLACK CHANNEL MAPPINGS
// ============================================================================

export const SLACK_CHANNELS: Record<string, string> = {
  CONTENT: '#winclap-support-content-tickets',
  GENERAL: '#winclap-support-tickets',
};

export function getSlackChannelForForm(formType: FormType): string {
  if (formType === 'content') {
    return SLACK_CHANNELS.CONTENT;
  }
  return SLACK_CHANNELS.GENERAL;
}

// ============================================================================
// FORM ASSIGNMENTS (Email-based)
// ============================================================================

export const FORM_ASSIGNMENTS: Record<FormType, string> = {
  // Content — también Danilo (Dante dejó la compañía, 02/09/2026)
  'content': 'danilo.bergoglio@winclap.com',

  // Hardware + usuarios (Kanban de Pedidos) — Danilo Bergoglio.
  // Áreas (departmental) — Franco Saldaño; van a Notion (Sol 01/09/2026).
  'user-onboarding': 'danilo.bergoglio@winclap.com',
  'user-offboarding': 'danilo.bergoglio@winclap.com',
  'user-management': 'danilo.bergoglio@winclap.com',
  'hardware-bug': 'danilo.bergoglio@winclap.com',
  'hardware-request': 'danilo.bergoglio@winclap.com',
  'hardware-sale': 'danilo.bergoglio@winclap.com',
  'access': 'franco.saldano@winclap.com',
  'sales': 'franco.saldano@winclap.com',
  'people': 'franco.saldano@winclap.com',
  'finance': 'franco.saldano@winclap.com',
  'strategy': 'franco.saldano@winclap.com',
  'owned-media': 'franco.saldano@winclap.com',
  'marketing': 'franco.saldano@winclap.com',
};

// ============================================================================
// PRIORITY LOGIC
// ============================================================================

export type PriorityLevel = 'Alta' | 'Media' | 'Baja';

// Key clients that automatically get "Alta" priority
const KEY_CLIENTS = {
  MERCADO: ['MERCADOPAGO', 'MERCADO PAGO', 'MP', 'MERCADOLIBRE', 'MERCADO LIBRE', 'MELI', 'ML'],
  DIDI: ['DIDI', 'DIDI RIDES', 'DIDIRIDES'],
  AVENUE: ['AVENUE', 'AVENUE SECURITIES', 'AVENUESECURITIES'],
  RAPPI: ['RAPPI'], // Matches RAPPI, RAPPI FOOD, etc.
};

export function calculatePriority(formType: FormType, formData: any = {}): PriorityLevel {
  // ALTA PRIORITY

  // Content → Always Alta
  if (formType === 'content') {
    return 'Alta';
  }

  // Errors → Always Alta
  if (formType.includes('error') || formType.includes('bug')) {
    return 'Alta';
  }

  // Key clients detection → Alta
  const clientContent = JSON.stringify(formData).toUpperCase();
  const allKeyClients = [
    ...KEY_CLIENTS.MERCADO,
    ...KEY_CLIENTS.DIDI,
    ...KEY_CLIENTS.AVENUE,
    ...KEY_CLIENTS.RAPPI,
  ];

  const hasKeyClient = allKeyClients.some((client) => clientContent.includes(client));
  if (hasKeyClient) {
    return 'Alta';
  }

  // User management → Alta
  if (formType.includes('user-') || formType === 'access') {
    return 'Alta';
  }

  // MEDIA PRIORITY

  // Specific form priority mappings
  const specificPriority: Record<string, PriorityLevel> = {
    'hardware-request': 'Media',
    'hardware-sale': 'Baja',
    sales: 'Media',
    people: 'Media',
    finance: 'Media',
    strategy: 'Media',
    'owned-media': 'Media',
    marketing: 'Media',
  };

  return specificPriority[formType] || 'Media';
}

// ============================================================================
// SLA DEADLINES (Business Days)
// ============================================================================

export function calculateSLADays(formType: FormType, priority: PriorityLevel): number {
  let daysToAdd = 3; // Default SLA

  switch (true) {
    case formType.includes('hardware-bug') || formType.includes('error'):
      daysToAdd = 1; // Critical issues
      break;
    case formType === 'content':
      daysToAdd = 2; // Content requests
      break;
    case formType.includes('access') || formType.includes('user-'):
      daysToAdd = 2; // User management
      break;
    case formType === 'hardware-request':
      daysToAdd = 3; // Hardware requests
      break;
    case formType === 'hardware-sale':
      daysToAdd = 5; // Hardware sales
      break;

    default:
      daysToAdd = 3; // Default for other requests
      break;
  }

  // No deadline for development tickets
  if (daysToAdd === 0) {
    return 0;
  }

  // Priority adjustments for key clients
  if (priority === 'Alta' && daysToAdd > 1) {
    daysToAdd = Math.max(1, Math.floor(daysToAdd / 2)); // Reduce by 50%, minimum 1 day
  }

  return daysToAdd;
}

export function calculateDeadlineDate(
  formType: FormType,
  priority: PriorityLevel,
  creationDate: Date = new Date()
): Date | null {
  const daysToAdd = calculateSLADays(formType, priority);

  // No deadline
  if (daysToAdd === 0) {
    return null;
  }

  // Días hábiles con el helper compartido (shared/dates), conservando la hora
  // de creación: el día se cuenta en UTC, igual que en Lambda (que corre en UTC).
  const day = creationDate.toISOString().slice(0, 10); // reuse-ok: es el día UTC de la fecha de creación (no "hoy"), como hacía el bucle viejo en Lambda
  const deadlineDay = addBusinessDaysISO(day, daysToAdd);
  const deadline = new Date(creationDate);
  deadline.setUTCFullYear(Number(deadlineDay.slice(0, 4)), Number(deadlineDay.slice(5, 7)) - 1, Number(deadlineDay.slice(8, 10)));
  return deadline;
}

// ============================================================================
// FORM TYPE DISPLAY NAMES
// ============================================================================

export const FORM_TYPE_NAMES: Record<FormType, string> = {
  'hardware-bug': 'Hardware - Error',
  'hardware-request': 'Solicitud de Hardware',
  'hardware-sale': 'Venta de Hardware',
  'user-onboarding': 'Gestión de alta de usuario',
  'user-offboarding': 'Gestión de baja de usuario',
  'user-management': 'Gestión de usuario',
  access: 'Accesos',
  sales: 'Growth Partners Request',
  people: 'People Request',
  finance: 'Finance Request',
  strategy: 'Strategy Request',
  'owned-media': 'Owned Media Request',
  content: 'Content Request',
  marketing: 'Marketing Request',
};

export function getFormTypeName(formType: FormType): string {
  return FORM_TYPE_NAMES[formType] || formType;
}

// ============================================================================
// TEAM ASSIGNMENTS
// ============================================================================

export function getTeamAssignment(formType: FormType): string {
  if (formType.startsWith('hardware-')) {
    return 'IT Team';
  } else if (formType.startsWith('user-') || formType === 'access') {
    return 'People Team';
  } else if (formType === 'sales') {
    return 'Sales Team';
  } else if (formType === 'people') {
    return 'People Team';
  } else if (formType === 'finance') {
    return 'Finance Team';
  } else if (formType === 'strategy') {
    return 'Strategy Team';
  } else if (formType === 'owned-media' || formType === 'content' || formType === 'marketing') {
    return 'Marketing Team';
  } else {
    return 'General Team';
  }
}
